import React, { useMemo, useContext } from 'react';
import moment from 'moment';
import { flatten } from 'lodash';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useProjects from '@ubeya/shared/hooks/account/useProjects';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import { isDateInRange } from '@ubeya/shared/utils/time';
import groupByData from 'shared/utils/payroll';
import DetailedInvoicesModal from 'shared/components/Invoices/DetailedInvoicesModal';

const CRMInvoiceModal = ({ clientBranchesIds, payroll = [], onClose, timeRange, countryCode, filePrefix = '' }) => {
  const { t, dateFormat } = useContext(ConfigContext);
  const { branches } = useBranches();
  const { mappedLabels } = useLabels();
  const { mappedLocations } = useLocations();
  const { mappedClientBranches } = useCRM();

  const { start, end } = timeRange;

  const { projects = [], employeesMap } = useProjects({
    timeRange: {
      startDate: start.format(API_DATE_FORMAT),
      endDate: end.format(API_DATE_FORMAT)
    },
    full: true,
    filters: { basic: [{ id: 10, value: clientBranchesIds }] },
    branchIds: (branches || []).map(({ id }) => id)
  });

  const mapPayroll = useMemo(
    () =>
      payroll.reduce((output, { mappedPayrollByTimesheetId = {} }) => {
        // mappedPayrollByTimesheetId is an object of keys (timesheetIds) and values (payroll value)
        Object.assign(output, mappedPayrollByTimesheetId);

        return output;
      }, {}),
    [payroll]
  );

  const slots = useMemo(
    () =>
      flatten(
        projects.map(
          ({
            id: projectId,
            invoiceNumber,
            labelId,
            clientBranchId,
            additionalRevenues,
            locationId,
            branchId,
            name,
            date: projectStartDate,
            endDate: projectEndDate,
            shifts: projectShifts = [],
            orders: projectOrders = []
          }) =>
            flatten(
              projectShifts
                .concat(
                  flatten(
                    projectOrders.map(({ shifts: orderShifts = [] }) =>
                      orderShifts.filter(({ date }) =>
                        isDateInRange({
                          startDate: start.format(API_DATE_FORMAT),
                          endDate: end.format(API_DATE_FORMAT),
                          date
                        })
                      )
                    )
                  )
                )
                .filter(({ isOverbooking }) => !isOverbooking)
                .map(({ locationId: shiftLocationId, comments, slots: shiftSlots = [] }) =>
                  flatten(
                    shiftSlots
                      .filter(({ employeeSlot, timesheet }) => !!employeeSlot && !!mapPayroll[timesheet.id])
                      .map((slot) => {
                        const projectHasName =
                          (labelId && mappedLabels[labelId]?.title !== mappedLocations[locationId]?.name) || name;

                        const projectName = projectHasName
                          ? labelId
                            ? mappedLabels[labelId]?.title
                            : name
                          : mappedLocations[locationId]?.name;

                        const { id: wageId, flatHours = 0, flatWage = 0, hourlyWage = 0 } =
                          mapPayroll[slot.timesheet.id].wage || {};

                        return {
                          ...slot,
                          clientWage: { id: wageId, flatHours, flatWage, hourlyWage },
                          date: slot?.startTime ? moment(slot.startTime).format(API_DATE_FORMAT) : projectStartDate,
                          clientBranchId,
                          comments,
                          locationId: shiftLocationId,
                          additionalRevenues,
                          projectId,
                          branchId,
                          name: projectName,
                          invoiceNumber,
                          projectStartDate,
                          projectEndDate
                        };
                      })
                  )
                )
            )
        )
      ),
    [mappedLabels, mappedLocations, projects, end, start, mapPayroll]
  );

  const groupedData = useMemo(
    () =>
      employeesMap
        ? groupByData({
            jobs: [],
            slots,
            employees: Object.values(employeesMap || {}),
            groupByKey: 'project',
            mapPayroll,
            mappedClientBranches,
            dateFormat,
            byClient: true,
            summarized: false,
            mappedLocations,
            mappedLabels
          })
        : [],
    [employeesMap, slots, mapPayroll, mappedClientBranches, dateFormat, mappedLocations, mappedLabels]
  );

  return (
    <DetailedInvoicesModal
      onClose={onClose}
      employeesMap={employeesMap}
      groupedData={groupedData}
      countryCode={countryCode}
      title={t('clientInvoice')}
      timeRange={timeRange}
      filePrefix={filePrefix}
    />
  );
};

export default CRMInvoiceModal;
